import { useEffect } from "react";
import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import Input from "../../../../../Components/Common/Input/Input";
import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import BudgetTable from "../BudgetTable";
import ComparisonTable from "../ComparisonTable";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import {
  PreconFormValues,
  BudgetRow,
  ComparisonRow,
} from "../../types/precon.types";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";
import { autoFillClass, prefillProps } from "../../utils/prefilled";

interface Props {
  prefilledFields: Set<string>;
}

const Step6BudgetAnalysis = ({ prefilledFields }: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();

  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  const calcPsf = (scheduledValue: string, totalSf: string): string => {
    const sv = parseFloat(scheduledValue.replace(/[$,\s]/g, ""));
    const sf = parseFloat(totalSf.replace(/[^0-9.]/g, ""));
    if (!sv || !sf) return "";
    return (sv / sf).toFixed(2);
  };

  const handleRowChange = (
    index: number,
    key: keyof BudgetRow,
    value: string,
  ) => {
    const updated = [...values.budgetRows];
    updated[index] = { ...updated[index], [key]: value };
    if (key === "value") {
      updated[index].projectPsf = calcPsf(value, values.totalSf);
    }
    setFieldValue("budgetRows", updated);
  };

  useEffect(() => {
    setFieldValue(
      "budgetRows",
      values.budgetRows.map((row) =>
        row.isTotal
          ? row
          : {
              ...row,
              projectSf: values.totalSf,
              projectPsf: calcPsf(row.value, values.totalSf),
            },
      ),
    );
    // only re-run when totalSf changes; budgetRows intentionally omitted to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.totalSf]);

  const handleComparisonRowChange = (
    index: number,
    key: keyof ComparisonRow,
    value: string,
  ) => {
    const updated = [...values.comparisonRows];
    updated[index] = { ...updated[index], [key]: value };
    setFieldValue("comparisonRows", updated);
  };

  const handleRemoveComparisonRow = (index: number) => {
    setFieldValue(
      "comparisonRows",
      values.comparisonRows.filter((_, i) => i !== index),
    );
  };

  const handleAddComparisonRow = () => {
    const newRow: ComparisonRow = {
      id: `comp-${Date.now()}`,
      project: "",
      units: "",
      budget: "",
      costLessSitework: "",
      costPerUnit: "",
      totalSf: "",
      costPerSf: "",
    };
    setFieldValue("comparisonRows", [...values.comparisonRows, newRow]);
  };

  return (
    <div className="screen active">
      <SectionHeader
        title="Construction Budget Analysis"
        subtitle="Major trade cost comparison and regional project benchmarking. Edit values as needed."
      />

      <Card
        title="Major Trade Cost Comparison"
        badge="Editable"
        badgeType="edit"
      >
        <BudgetTable rows={values.budgetRows} onRowChange={handleRowChange} />
        <div className="field tableNote">
          <label>
            Budget Analysis Narrative <span className="req">*</span>
          </label>
          <TextArea
            name="budgetNarrative"
            className={`ft textareaM${hasErr("budgetNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.budgetNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.budgetNarrative}
            touched={isTouched("budgetNarrative")}
            error={err("budgetNarrative")}
          />
        </div>
      </Card>

      <Card
        title="Comparison to Similar Regional Projects"
        badge="Editable"
        badgeType="edit"
      >
        <ComparisonTable
          rows={values.comparisonRows}
          onRowChange={handleComparisonRowChange}
          onAddRow={handleAddComparisonRow}
          onRemoveRow={handleRemoveComparisonRow}
          touched={!!touched.comparisonRows}
          error={err("comparisonRows")}
          rowErrors={
            Object.keys(touched).some((k) => k.startsWith("comparisonRows"))
              ? values.comparisonRows.map((row) =>
                  !row.isThis && !row.project.trim()
                    ? "Fill in the project name or remove this row"
                    : undefined,
                )
              : undefined
          }
        />
        <div className="field tableNote">
          <label>
            Budget Analysis Conclusion <span className="req">*</span>
          </label>
          <TextArea
            name="budgetConclusion"
            className={`ft textareaM${hasErr("budgetConclusion", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.budgetConclusion}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.budgetConclusion}
            touched={isTouched("budgetConclusion")}
            error={err("budgetConclusion")}
          />
        </div>
        <div className="insightBox">
          <strong>CSC Finding</strong> Review comparable projects and adjust the
          conclusion narrative accordingly.
        </div>
      </Card>

      {values.borrowerEquityRequired && (
        <Card title="Borrower Fund" badge="From GC Checklist" badgeType="auto">
          <div className="fieldRow c2">
            <div className="field">
              <label>Amount</label>
              <Input
                name="borrowerEquityAmount"
                className={`${autoFillClass("borrowerEquityAmount", prefilledFields)}${hasErr("borrowerEquityAmount", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
                value={values.borrowerEquityAmount}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={CHAR_LIMIT.SHORT}
                {...prefillProps("borrowerEquityAmount", prefilledFields)}
              />
              <ErrorMessage
                value={values.borrowerEquityAmount}
                have={CHAR_LIMIT.SHORT}
                touched={isTouched("borrowerEquityAmount")}
                error={err("borrowerEquityAmount")}
              />
            </div>
          </div>
          <div className="field tableNote">
            <label>Description</label>
            <TextArea
              name="borrowerEquityDescription"
              className={`${autoFillClass("borrowerEquityDescription", prefilledFields, "ft")} textareaM${hasErr("borrowerEquityDescription", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
              value={values.borrowerEquityDescription}
              onChange={handleChange}
              onBlur={handleBlur}
              maxChars={CHAR_LIMIT.DESCRIPTION}
              {...prefillProps("borrowerEquityDescription", prefilledFields)}
            />
            <ErrorMessage
              value={values.borrowerEquityDescription}
              touched={isTouched("borrowerEquityDescription")}
              error={err("borrowerEquityDescription")}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default Step6BudgetAnalysis;
